// Rename a session (Session options → Rename session…), on the shared Dialog and the shared field.
// Opens with the field focused and the name selected; Enter saves, Esc, Cancel, the close button and a click
// outside close it, and the caller puts focus back on the ⋯ button. A name can't be empty (a session that was
// named by hand can go back to its automatic name instead).

import { IconRotateClockwise } from '@tabler/icons-react';
import { useEffect, useRef, useState, type SyntheticEvent } from 'react';
import { createPortal } from 'react-dom';
import { Dialog } from './Dialog';
import { Button, Ic } from './ds';

export function RenameDialog({ open, name, automatic, onSave, onClose }: { open: boolean; name: string; automatic: boolean; onSave: (name: string) => void; onClose: () => void }) {
  const [draft, setDraft] = useState(name);
  const input = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    setDraft(name);
    // the Dialog has focused the field by now: select the name
    input.current?.select();
  }, [open, name]);

  if (!open) return null;

  const next = draft.trim();
  const save = () => {
    if (!next) return;
    if (next !== name) onSave(next);
    onClose();
  };
  // it renders from a sidebar row: its clicks and keys stay here, not on the row
  const keep = (e: SyntheticEvent) => e.stopPropagation();

  return createPortal(
    <div style={{ display: 'contents' }} onClick={keep} onDoubleClick={keep} onPointerDown={keep} onKeyDown={keep}>
      <Dialog title="Rename session" onClose={onClose} testId="rename-dialog">
        <form
          className="dlg-form"
          onSubmit={(e) => {
            e.preventDefault();
            save();
          }}
        >
          <input
            ref={input}
            className="input"
            value={draft}
            autoComplete="off"
            spellCheck={false}
            aria-label="Session name"
            data-testid="rename-input"
            onChange={(e) => setDraft(e.target.value)}
          />
          <div className="dlg-foot">
            {automatic && (
              <Button
                className="dlg-foot-start"
                data-testid="rename-auto"
                onClick={() => {
                  onSave('');
                  onClose();
                }}
              >
                <Ic icon={IconRotateClockwise} size={16} />
                Use automatic name
              </Button>
            )}
            <Button type="button" data-testid="rename-cancel" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" disabled={!next} data-testid="rename-save">
              Rename
            </Button>
          </div>
        </form>
      </Dialog>
    </div>,
    document.body,
  );
}
